import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import CameraScannerButton from "@/components/CameraScannerButton";
import { beep } from "@/lib/beep";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, ShoppingCart, Trash2, PackageCheck, Undo2, Search, ScanLine, Pencil, Printer, Eye, X } from "lucide-react";
import { fmtDate } from "@/lib/meta";
import { buildPedidoCompraHTML, openPrint } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS = {
  CRIADO: { label: "Criado", cls: "text-slate-300 bg-slate-800/80 border-slate-600" },
  AGUARDANDO_RECEBIMENTO: { label: "Aguardando Recebimento", cls: "text-amber-300 bg-amber-950/80 border-amber-500" },
  RECEBIDO_PARCIAL: { label: "Recebido Parcial", cls: "text-sky-300 bg-sky-950/80 border-sky-500" },
  RECEBIDO: { label: "Recebido", cls: "text-teal-300 bg-teal-950/80 border-teal-500" },
  DEVOLUCAO_PARCIAL: { label: "Devolução Parcial", cls: "text-orange-300 bg-orange-950/80 border-orange-500" },
  DEVOLVIDO: { label: "Devolvido", cls: "text-rose-300 bg-rose-950/80 border-rose-500" },
  FINALIZADO: { label: "Finalizado", cls: "text-emerald-200 bg-emerald-900/90 border-emerald-400" },
  CANCELADO: { label: "Cancelado", cls: "text-red-300 bg-red-950/80 border-red-600" },
};
const Badge = ({ s }) => { const m = STATUS[s] || STATUS.CRIADO; return <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase font-mono ${m.cls}`}>{m.label}</span>; };
const MOTIVOS = [
  { id: "PRODUTO_ERRADO", label: "Produto errado" }, { id: "AVARIADO", label: "Produto avariado" },
  { id: "VALIDADE", label: "Validade" }, { id: "QUALIDADE", label: "Qualidade" },
  { id: "QTD_EXCEDENTE", label: "Quantidade excedente" }, { id: "ERRO_COMPRA", label: "Erro de compra" }, { id: "OUTRO", label: "Outro" },
];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const totalValor = (c) => c.itens.reduce((s, i) => s + i.qtd_solicitada * (i.custo_unitario || 0), 0);
const recebido = (i) => i.qtd_recebida || 0;
const devolvido = (i) => i.qtd_devolvida || 0;
const semRecebimento = (c) => !c.itens.some((i) => recebido(i) > 0);
const pendenteTotal = (c) => c.itens.reduce((s, i) => s + (i.qtd_solicitada - recebido(i)), 0);

export default function Compras() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [forns, setForns] = useState([]);
  const [units, setUnits] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ fornecedor_id: "", observacao: "", itens: [] });
  const [busca, setBusca] = useState("");
  const [saving, setSaving] = useState(false);
  const [receber, setReceber] = useState(null); // compra em recebimento
  const [rmap, setRmap] = useState({});
  const [devolver, setDevolver] = useState(null); // compra em devolução
  const [dmap, setDmap] = useState({});
  const [dmotivo, setDmotivo] = useState("PRODUTO_ERRADO");
  const [dobs, setDobs] = useState("");
  const [ver, setVer] = useState(null); // visualizar
  const [imprimir, setImprimir] = useState(null);
  const previewHtml = useMemo(() => {
    if (!imprimir) return "";
    const token = localStorage.getItem("nexolog_token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const cd = units.find((u) => u.id === imprimir.cd_id);
    const forn = forns.find((f) => f.id === imprimir.fornecedor_id) || null;
    const resp = (imprimir.historico || []).find((h) => h.status === "CRIADO")?.usuario || "—";
    const logoUrl = cd?.logo_path ? `${backend}/api/files/${cd.logo_path}?auth=${token}` : null;
    return buildPedidoCompraHTML({ compra: imprimir, empresaNome: user?.tenant_nome || "NEXO LOG", logoUrl, cdNome: imprimir.cd_nome || cd?.nome, fornecedor: forn, responsavel: resp, statusLabel: STATUS[imprimir.status]?.label });
  }, [imprimir, units, forns, user]);
  const opRef = useState(() => ({ id: null }))[0];
  const [filtros, setFiltros] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return { data_ini: d.toISOString().slice(0, 10), data_fim: "", fornecedor_id: "", status: "", q: "" };
  });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) p.append(k, v); });
    setRows(null);
    api.get(`/compras?${p.toString()}`).then((r) => setRows(r.data));
  }, [filtros]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/fornecedores").then((r) => setForns(r.data.filter((f) => f.ativo)));
    api.get("/produtos").then((r) => setProdutos(r.data));
    api.get("/units").then((r) => setUnits(r.data));
  }, []);

  const setF = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));
  const atalho = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); setFiltros((f) => ({ ...f, data_ini: d.toISOString().slice(0, 10), data_fim: "" })); };
  const esteMes = () => { const d = new Date(); setFiltros((f) => ({ ...f, data_ini: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10), data_fim: "" })); };
  const limpar = () => setFiltros({ data_ini: "", data_fim: "", fornecedor_id: "", status: "", q: "" });

  const setItem = (i, k, v) => setForm((f) => ({ ...f, itens: f.itens.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const rmItem = (i) => setForm((f) => ({ ...f, itens: f.itens.filter((_, idx) => idx !== i) }));
  const results = useMemo(() => {
    const b = busca.trim().toLowerCase();
    if (!b) return [];
    return produtos.filter((p) => (p.nome || "").toLowerCase().includes(b) || (p.sku || "").toLowerCase().includes(b) || (p.ean || "").includes(b) || (p.ean_master || "").includes(b) || (p.marca || "").toLowerCase().includes(b)).slice(0, 15);
  }, [produtos, busca]);
  const addProduto = (p, inc = 1) => setForm((f) => {
    const existe = f.itens.find((it) => it.sku_id === p.id);
    if (existe) return { ...f, itens: f.itens.map((it) => it.sku_id === p.id ? { ...it, qtd_solicitada: (Number(it.qtd_solicitada) || 0) + inc } : it) };
    return { ...f, itens: [...f.itens, { sku_id: p.id, qtd_solicitada: inc, custo_unitario: 0 }] };
  });
  const doScan = (codeArg) => {
    const code = (codeArg ?? busca).trim(); if (!code) return;
    const p = produtos.find((x) => x.ean === code || x.sku === code || x.ean_master === code);
    if (!p) { toast.error("Produto não encontrado no cadastro."); return; }
    try { beep(); } catch (_) {}
    addProduto(p, p.ean_master === code ? (p.fator_conversao || 1) : 1); setBusca("");
  };

  const abrirNovo = () => { setEditId(null); setForm({ fornecedor_id: "", observacao: "", itens: [] }); setBusca(""); setModal(true); };
  const abrirEdicao = (c) => {
    setEditId(c.id);
    setForm({ fornecedor_id: c.fornecedor_id, observacao: c.observacao || "", itens: c.itens.map((i) => ({ sku_id: i.sku_id, qtd_solicitada: i.qtd_solicitada, custo_unitario: i.custo_unitario || 0 })) });
    setBusca(""); setModal(true);
  };
  const salvar = async () => {
    if (!form.fornecedor_id) return toast.error("Selecione o fornecedor");
    const itens = form.itens.filter((i) => i.sku_id && Number(i.qtd_solicitada) > 0).map((i) => ({ sku_id: i.sku_id, qtd_solicitada: Number(i.qtd_solicitada), custo_unitario: Number(i.custo_unitario) || 0 }));
    if (!itens.length) return toast.error("Adicione ao menos um produto");
    setSaving(true);
    try {
      if (editId) { await api.put(`/compras/${editId}`, { fornecedor_id: form.fornecedor_id, observacao: form.observacao, itens }); toast.success("Pedido atualizado"); }
      else { await api.post("/compras", { fornecedor_id: form.fornecedor_id, observacao: form.observacao, itens }); toast.success("Pedido de compra criado"); }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  const excluir = async (c) => {
    if (!window.confirm(`Excluir o pedido ${c.numero}? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/compras/${c.id}`); toast.success("Pedido excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const abrirReceber = (c) => { setReceber(c); setRmap({}); opRef.id = uid(); };
  const confirmarReceber = async () => {
    const itens = Object.entries(rmap).filter(([, v]) => Number(v) > 0).map(([item_id, v]) => ({ item_id, qtd: Number(v) }));
    if (!itens.length) return toast.error("Informe as quantidades recebidas");
    setSaving(true);
    try { await api.post(`/compras/${receber.id}/receber`, { itens, op_id: opRef.id }); toast.success("Recebimento confirmado — estoque atualizado"); setReceber(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const abrirDevolver = (c) => { setDevolver(c); setDmap({}); setDmotivo("PRODUTO_ERRADO"); setDobs(""); opRef.id = uid(); };
  const confirmarDevolver = async () => {
    const itens = Object.entries(dmap).filter(([, v]) => Number(v) > 0).map(([item_id, v]) => ({ item_id, qtd: Number(v) }));
    if (!itens.length) return toast.error("Informe as quantidades a devolver");
    if (dmotivo === "OUTRO" && !dobs.trim()) return toast.error("Para 'Outro', informe a observação");
    setSaving(true);
    try { await api.post(`/compras/${devolver.id}/devolver`, { itens, motivo: dmotivo, observacao: dobs, op_id: opRef.id }); toast.success("Devolução confirmada — estoque reduzido"); setDevolver(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const printCompra = (compra) => {
    const token = localStorage.getItem("nexolog_token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const cd = units.find((u) => u.id === compra.cd_id);
    const forn = forns.find((f) => f.id === compra.fornecedor_id) || null;
    const resp = (compra.historico || []).find((h) => h.status === "CRIADO")?.usuario || (compra.historico || [])[0]?.usuario || "—";
    const logoUrl = cd?.logo_path ? `${backend}/api/files/${cd.logo_path}?auth=${token}` : null;
    const html = buildPedidoCompraHTML({
      compra, empresaNome: user?.tenant_nome || user?.empresa || "NEXO LOG",
      logoUrl, cdNome: compra.cd_nome || cd?.nome, fornecedor: forn, responsavel: resp,
      statusLabel: STATUS[compra.status]?.label,
    });
    openPrint(html);
  };

  return (
    <div className="space-y-5" data-testid="compras-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Estoque</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><ShoppingCart className="w-8 h-8 text-primary" /> Entrada / Pedido de Compra</h1>
        </div>
        <Button onClick={abrirNovo} data-testid="btn-nova-compra"><Plus className="w-4 h-4" /> Novo Pedido de Compra</Button>
      </div>

      {/* Filtros */}
      <Card className="bg-card/70 p-3 space-y-3" data-testid="compras-filtros">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div><Label className="text-[10px]">Data inicial</Label><Input type="date" value={filtros.data_ini} onChange={(e) => setF("data_ini", e.target.value)} data-testid="f-data-ini" /></div>
          <div><Label className="text-[10px]">Data final</Label><Input type="date" value={filtros.data_fim} onChange={(e) => setF("data_fim", e.target.value)} data-testid="f-data-fim" /></div>
          <div><Label className="text-[10px]">Fornecedor</Label>
            <Select value={filtros.fornecedor_id || "TODOS"} onValueChange={(v) => setF("fornecedor_id", v === "TODOS" ? "" : v)}>
              <SelectTrigger data-testid="f-fornecedor"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="TODOS">Todos</SelectItem>{forns.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px]">Status</Label>
            <Select value={filtros.status || "TODOS"} onValueChange={(v) => setF("status", v === "TODOS" ? "" : v)}>
              <SelectTrigger data-testid="f-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="TODOS">Todos</SelectItem>{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px]">Buscar</Label><Input value={filtros.q} onChange={(e) => setF("q", e.target.value)} placeholder="Nº, fornecedor, nota" data-testid="f-busca" /></div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={() => atalho(0)} data-testid="atalho-hoje">Hoje</Button>
          <Button size="sm" variant="outline" onClick={() => atalho(7)} data-testid="atalho-7">Últimos 7 dias</Button>
          <Button size="sm" variant="outline" onClick={() => atalho(30)} data-testid="atalho-30">Últimos 30 dias</Button>
          <Button size="sm" variant="outline" onClick={esteMes} data-testid="atalho-mes">Este mês</Button>
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={limpar} data-testid="atalho-limpar"><X className="w-3 h-3" /> Limpar filtros</Button>
        </div>
      </Card>

      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid gap-3">
          {rows.map((c) => {
            const total = c.itens.reduce((s, i) => s + i.qtd_solicitada, 0);
            const rec = c.itens.reduce((s, i) => s + recebido(i), 0);
            const noRec = semRecebimento(c);
            const encerrado = ["CANCELADO", "FINALIZADO"].includes(c.status);
            const temPendente = pendenteTotal(c) > 0;
            const podeDevolver = rec - c.itens.reduce((s, i) => s + devolvido(i), 0) > 0;
            return (
              <Card key={c.id} className="bg-card/70 p-4" data-testid={`compra-card-${c.numero}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-base sm:text-lg">{c.numero}</div>
                    <div className="text-xs text-muted-foreground">{c.fornecedor_nome} · {fmtDate(c.data || c.created_at)} · {c.itens.length} itens · R$ {totalValor(c).toFixed(2)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Recebido {rec}/{total}</div>
                  </div>
                  <Badge s={c.status} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {noRec && !encerrado ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(c)} data-testid={`btn-editar-${c.numero}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                      {["MASTER", "SUPER_ADMIN"].includes(user.role) && <Button size="sm" variant="outline" className="text-red-400" onClick={() => excluir(c)} data-testid={`btn-excluir-${c.numero}`}><Trash2 className="w-3.5 h-3.5" /> Excluir</Button>}
                      <Button size="sm" variant="outline" onClick={() => setImprimir(c)} data-testid={`btn-imprimir-${c.numero}`}><Printer className="w-3.5 h-3.5" /> Imprimir</Button>
                      <Button size="sm" onClick={() => abrirReceber(c)} data-testid={`btn-receber-${c.numero}`}><PackageCheck className="w-3.5 h-3.5" /> Receber Pedido</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setVer(c)} data-testid={`btn-ver-${c.numero}`}><Eye className="w-3.5 h-3.5" /> Visualizar</Button>
                      <Button size="sm" variant="outline" onClick={() => setImprimir(c)} data-testid={`btn-imprimir-${c.numero}`}><Printer className="w-3.5 h-3.5" /> Imprimir</Button>
                      {temPendente && !encerrado && <Button size="sm" onClick={() => abrirReceber(c)} data-testid={`btn-receber-rest-${c.numero}`}><PackageCheck className="w-3.5 h-3.5" /> Receber Restante</Button>}
                      {podeDevolver && !encerrado && <Button size="sm" variant="outline" className="text-orange-300" onClick={() => abrirDevolver(c)} data-testid={`btn-devolver-${c.numero}`}><Undo2 className="w-3.5 h-3.5" /> Devolver Pedido</Button>}
                    </>
                  )}
                </div>
              </Card>
            );
          })}
          {!rows.length && <Card className="p-10 text-center text-muted-foreground">Nenhum pedido de compra no período/filtro selecionado.</Card>}
        </div>
      )}

      {/* Criar / Editar */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar Pedido de Compra" : "Novo Pedido de Compra"}</DialogTitle>
            <DialogDescription>A entrada no estoque do CD ocorre somente no recebimento físico.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Fornecedor</Label>
              <Select value={form.fornecedor_id} onValueChange={(v) => setForm((f) => ({ ...f, fornecedor_id: v }))}>
                <SelectTrigger data-testid="compra-fornecedor"><SelectValue placeholder="Selecione o fornecedor" /></SelectTrigger>
                <SelectContent>{forns.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Produtos</Label>
              <div className="rounded-lg border border-primary/40 bg-black/40 p-3 space-y-2">
                <div className="flex items-center gap-2 text-primary text-[11px] font-bold uppercase tracking-[0.18em]"><ScanLine className="w-3.5 h-3.5" /> Localizar / Bipar produto</div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), doScan())} placeholder="Nome, SKU, EAN, Master ou marca" data-testid="compra-busca" className="pl-9 h-11 font-mono" />
                  </div>
                  <CameraScannerButton continuous onDetected={(c) => doScan(c)} className="h-11 px-4 shrink-0" testid="compra-camera-btn" />
                </div>
                {busca.trim() && (
                  <div className="space-y-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card/40 p-1.5" data-testid="compra-resultados">
                    {results.map((p) => (
                      <button key={p.id} type="button" onClick={() => { addProduto(p, 1); setBusca(""); }} data-testid={`compra-add-${p.sku}`} className="w-full text-left rounded-md hover:bg-secondary/50 px-3 py-2 flex items-center justify-between">
                        <div className="min-w-0"><div className="text-sm font-medium truncate">{p.nome}</div><div className="text-[10px] font-mono text-muted-foreground truncate">SKU: {p.sku}{p.ean ? ` • EAN: ${p.ean}` : ""}{p.marca ? ` • ${p.marca}` : ""}</div></div>
                        <Plus className="w-4 h-4 text-primary shrink-0" />
                      </button>
                    ))}
                    {!results.length && <div className="text-sm text-muted-foreground p-3">Produto não encontrado no cadastro.</div>}
                  </div>
                )}
              </div>
              {form.itens.map((it, i) => {
                const p = produtos.find((x) => x.id === it.sku_id);
                return (
                  <div key={it.sku_id || i} className="grid grid-cols-12 gap-2 items-end border border-border rounded-md p-2" data-testid={`compra-item-row-${i}`}>
                    <div className="col-span-6 min-w-0"><Label className="text-[10px]">Produto</Label><div className="text-sm font-medium truncate">{p?.nome || "—"}</div><div className="text-[10px] font-mono text-muted-foreground truncate">{p?.sku}</div></div>
                    <div className="col-span-2"><Label className="text-[10px]">Qtd</Label><Input type="number" min={1} value={it.qtd_solicitada} onChange={(e) => setItem(i, "qtd_solicitada", e.target.value)} data-testid={`compra-item-qtd-${i}`} /></div>
                    <div className="col-span-3"><Label className="text-[10px]">Custo un.</Label><Input type="number" value={it.custo_unitario} onChange={(e) => setItem(i, "custo_unitario", e.target.value)} data-testid={`compra-item-custo-${i}`} /></div>
                    <div className="col-span-1"><Button variant="ghost" size="icon" className="text-red-400" onClick={() => rmItem(i)} data-testid={`compra-item-del-${i}`}><Trash2 className="w-4 h-4" /></Button></div>
                  </div>
                );
              })}
              {!form.itens.length && <div className="text-xs text-muted-foreground">Nenhum item. Localize ou bipe um produto acima.</div>}
            </div>
            <div><Label>Observação</Label><Input value={form.observacao} onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))} data-testid="compra-obs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={saving} data-testid="btn-salvar-compra">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Salvar Alterações" : "Criar Pedido")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receber */}
      <Dialog open={!!receber} onOpenChange={(o) => !o && setReceber(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="receber-modal">
          {receber && (<>
            <DialogHeader><DialogTitle className="font-display uppercase">Receber — {receber.numero}</DialogTitle>
              <DialogDescription>{receber.fornecedor_nome} · CD: {receber.cd_nome}. Confirmar dá entrada real no estoque do CD.</DialogDescription></DialogHeader>
            <div className="space-y-2">
              {receber.itens.map((it) => {
                const pend = it.qtd_solicitada - recebido(it);
                return (
                  <div key={it.id} className="border border-border rounded-md p-2.5" data-testid={`receber-item-${it.sku}`}>
                    <div className="text-sm font-medium">{it.nome} <span className="text-[10px] font-mono text-muted-foreground">{it.sku}</span></div>
                    <div className="grid grid-cols-5 gap-2 mt-1.5 items-end text-center">
                      <div><div className="text-[9px] uppercase text-muted-foreground">Pedida</div><div className="font-mono font-bold">{it.qtd_solicitada}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Já receb.</div><div className="font-mono font-bold">{recebido(it)}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Pendente</div><div className="font-mono font-bold text-amber-300">{pend}</div></div>
                      <div className="col-span-1"><div className="text-[9px] uppercase text-muted-foreground">Custo</div><div className="font-mono">{(it.custo_unitario || 0).toFixed(2)}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Recebendo</div><Input type="number" min={0} max={pend} disabled={pend <= 0} value={rmap[it.id] ?? ""} onChange={(e) => setRmap((r) => ({ ...r, [it.id]: e.target.value }))} className="h-9 text-center" data-testid={`receber-input-${it.sku}`} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceber(null)}>Cancelar</Button>
              <Button onClick={confirmarReceber} disabled={saving} data-testid="btn-confirmar-receber">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PackageCheck className="w-4 h-4" /> Confirmar Recebimento</>}</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Devolver */}
      <Dialog open={!!devolver} onOpenChange={(o) => !o && setDevolver(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="devolver-modal">
          {devolver && (<>
            <DialogHeader><DialogTitle className="font-display uppercase">Devolver ao Fornecedor — {devolver.numero}</DialogTitle>
              <DialogDescription>{devolver.fornecedor_nome}. Confirmar dá saída real do estoque do CD.</DialogDescription></DialogHeader>
            <div className="space-y-2">
              {devolver.itens.filter((it) => recebido(it) - devolvido(it) > 0).map((it) => {
                const disp = recebido(it) - devolvido(it);
                return (
                  <div key={it.id} className="border border-border rounded-md p-2.5" data-testid={`devolver-item-${it.sku}`}>
                    <div className="text-sm font-medium">{it.nome} <span className="text-[10px] font-mono text-muted-foreground">{it.sku}</span></div>
                    <div className="grid grid-cols-4 gap-2 mt-1.5 items-end text-center">
                      <div><div className="text-[9px] uppercase text-muted-foreground">Recebido</div><div className="font-mono font-bold">{recebido(it)}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Já devolv.</div><div className="font-mono font-bold">{devolvido(it)}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Disponível</div><div className="font-mono font-bold text-orange-300">{disp}</div></div>
                      <div><div className="text-[9px] uppercase text-muted-foreground">Devolver</div><Input type="number" min={0} max={disp} value={dmap[it.id] ?? ""} onChange={(e) => setDmap((r) => ({ ...r, [it.id]: e.target.value }))} className="h-9 text-center" data-testid={`devolver-input-${it.sku}`} /></div>
                    </div>
                  </div>
                );
              })}
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label>Motivo</Label>
                  <Select value={dmotivo} onValueChange={setDmotivo}>
                    <SelectTrigger data-testid="devolver-motivo"><SelectValue /></SelectTrigger>
                    <SelectContent>{MOTIVOS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Observação {dmotivo === "OUTRO" && <span className="text-red-400">*</span>}</Label><Input value={dobs} onChange={(e) => setDobs(e.target.value)} data-testid="devolver-obs" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDevolver(null)}>Cancelar</Button>
              <Button className="bg-orange-500 hover:bg-orange-400 text-black" onClick={confirmarDevolver} disabled={saving} data-testid="btn-confirmar-devolver">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-4 h-4" /> Confirmar Devolução</>}</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Visualizar (histórico) */}
      <Dialog open={!!ver} onOpenChange={(o) => !o && setVer(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ver-modal">
          {ver && (<>
            <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2">{ver.numero} <Badge s={ver.status} /></DialogTitle>
              <DialogDescription>{ver.fornecedor_nome}</DialogDescription></DialogHeader>
            <div className="space-y-1.5">
              {ver.itens.map((it) => (
                <div key={it.id} className="text-xs font-mono flex justify-between border-b border-border pb-1">
                  <span className="truncate">{it.nome}</span>
                  <span className="text-muted-foreground shrink-0 ml-2">ped {it.qtd_solicitada} · rec {recebido(it)} · dev {devolvido(it)}</span>
                </div>
              ))}
            </div>
            <div className="font-display text-sm font-bold uppercase mt-2">Histórico</div>
            <div className="space-y-1">
              {(ver.historico || []).map((h, i) => (
                <div key={i} className="text-xs flex justify-between border-b border-border pb-1">
                  <span>{STATUS[h.status]?.label || h.status}{h.detalhe ? ` — ${h.detalhe}` : ""}</span>
                  <span className="text-muted-foreground">{h.usuario} · {fmtDate(h.timestamp)}</span>
                </div>
              ))}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Imprimir Pedido de Compra — pré-visualização A4 */}
      <Dialog open={!!imprimir} onOpenChange={(o) => !o && setImprimir(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="imprimir-modal">
          {imprimir && (<>
            <DialogHeader><DialogTitle className="font-display uppercase">Pedido de Compra — Documento A4</DialogTitle>
              <DialogDescription>Pré-visualização em proporção A4. Imprima ou salve como PDF pelo diálogo do navegador.</DialogDescription></DialogHeader>
            <div className="bg-white rounded-md overflow-hidden border border-border" style={{ aspectRatio: "210 / 297" }}>
              <iframe title="preview" className="w-full h-full" style={{ border: 0 }} srcDoc={previewHtml} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImprimir(null)}>Fechar</Button>
              <Button onClick={() => printCompra(imprimir)} data-testid="btn-imprimir-confirmar"><Printer className="w-4 h-4" /> Imprimir / Salvar PDF</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
